import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { postMasqueradeUser } from '../../actions/auth';
import '../../css/AdminDashboard.css';
import caretDown from '../../img/caret-down.svg';
import caretUp from '../../img/caret-up.svg';
import copyIcon from '../../img/copy.svg'; 
import MainWrapper from '../wrappers/mainWrapper';

const AdminDashboard = props => {
    const [filter, setFilter] = useState(true);
    const [activeUsers, setActiveUsers] = useState([]);
    const [inactiveUsers, setInactiveUsers] = useState([]);
    const [searchByEmail, setSearchByEmail] = useState("");
    const [nameSort, setNameSort] = useState(false);
    const [emailSort, setEmailSort] = useState(false);
    const [dateSort, setDateSort] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [matches, setMatches] = useState("");
    const [userTally, setUserTally] = useState({ freemium: 0, subscribed: 0, internal: 0 });
    const [hoveredUser, setHoveredUser] = useState(null);
    const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 });
    const [isHoveringModal, setIsHoveringModal] = useState(false);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentUser = useSelector(state => state.user);

    useEffect(() => {
        getApiData();
        getInactive();
    }, []);

    useEffect(() => {
        const usersToFilter = filter ? activeUsers : inactiveUsers;
        const filtered = usersToFilter.filter(user =>
            user.email.toLowerCase().includes(searchByEmail.toLowerCase())
        );
        setFilteredUsers(filtered);
        setMatches(filtered.length === 0 ? '0 Matches' : '');

        if (filter && activeUsers.length) {
            const tally = { freemium: 0, subscribed: 0, internal: 0 };
            activeUsers.forEach(user => {
                const type = classifyUser(user);
                if (type.startsWith("Subscribed")) tally.subscribed++;
                else if (type === "Internal") tally.internal++;
                else tally.freemium++;
            });
            setUserTally(tally);
        }
    }, [searchByEmail, filter, activeUsers, inactiveUsers]);

    const sortByDateDesc = (users = []) => {
        const getTime = (u) => {
            if (!u?.date_created) return null;
            const t = new Date(u.date_created).getTime();
            return isNaN(t) ? null : t;
        };
        return [...users].sort((a, b) => {
            const aTime = getTime(a);
            const bTime = getTime(b);
            if (aTime === null && bTime === null) return 0;
            if (aTime === null) return 1;
            if (bTime === null) return -1;
            return bTime - aTime;
        });
    };

    const getApiData = async () => {
        const response = await fetch(`${process.env.REACT_APP_ACTIVE_USR}`).then(res => res.json());
        setActiveUsers(sortByDateDesc(response.user));
    };

    const getInactive = async () => {
        const response = await fetch(`${process.env.REACT_APP_INACTIVE_USR}`).then(res => res.json());
        setInactiveUsers(sortByDateDesc(response.inactiveuser));
    };

    const classifyUser = (user) => {
        const email = (user.email || "").toLowerCase();
        if (email.includes("elkmont") || email.includes("getlevlr.com") || email.includes("test") || email.includes("example") || email.includes("pdgcreative.com")) {
            return "Internal";
        }

        if (user.baseSubscription) {
            const discount = user.baseSubscription.discount || null;
            const coupon = discount?.coupon || null;
            let couponText = '';
            if (coupon) {
                const couponName = coupon.name || coupon.id || "Coupon";
                couponText = ` (Coupon: ${couponName})`;
            }
            return `Subscribed${couponText}`;
        }

        return "Freemium";
    };

    const getUserTypeColor = (userType) => {
        if (userType.startsWith("Subscribed") && userType.includes("Coupon")) return 'purple';
        if (userType.startsWith("Subscribed")) return 'blue';
        if (userType === "Freemium") return 'green';
        if (userType === "Internal") return 'gray';
        return 'black';
    };

    const exportToCSV = () => {
        if (!activeUsers || activeUsers.length === 0) {
            alert("No data available to export.");
            return;
        }

        const excludedFields = ['_id', 'password', 'resetPasswordToken', 'resetPasswordExpires'];
        let headers = Object.keys(activeUsers[0]).filter(key => !excludedFields.includes(key));
        headers.push('user_type', 'primaryDecisionMaker', 'date_created');

        const csvContent = "data:text/csv;charset=utf-8," + 
            [headers.join(",")].concat(activeUsers.map(user => 
                headers.map(header => {
                    if (header === 'user_type') return classifyUser(user);
                    if (header === 'primaryDecisionMaker') return user.primaryDecisionMaker || '';
                    if (header === 'date_created') return user.date_created || '';
                    return user[header] || '';
                }).join(",")
            )).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "active_users.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFilterChange = (event) => {
        const newFilter = event.target.value === 'true';
        setFilter(newFilter);

        const usersToFilter = newFilter ? activeUsers : inactiveUsers;
        const filtered = usersToFilter.filter(user =>
            user.email.toLowerCase().includes(searchByEmail.toLowerCase())
        );
        setFilteredUsers(filtered);
        setMatches(filtered.length === 0 ? '0 Matches' : '');
    };

    function goToAccount(id) { navigate(`/editAccount/${id}`); }

    function formatPhoneNumber(phone) {
        const cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone.length === 10) return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
        return phone;
    }

    const search = () => {
        const usersToFilter = filter ? activeUsers : inactiveUsers;
        const filtered = usersToFilter.filter(user => user.email.toLowerCase().includes(searchByEmail.toLowerCase()));
        setFilteredUsers(filtered);
        setMatches(filtered?.length === 0 ? '0 Matches' : '');
    }

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert("Copied to clipboard!");
    };

    // RESTORE OLD SORTING LOGIC
    function sortName() {
        const isActive = filter;
        const users = isActive ? [...activeUsers] : [...inactiveUsers];

        const sorted = nameSort
            ? users.sort((a, b) => b.name.localeCompare(a.name))
            : users.sort((a, b) => a.name.localeCompare(b.name));

        if (isActive) setActiveUsers(sorted);
        else setInactiveUsers(sorted);

        setNameSort(!nameSort);
    }

    function sortEmail() {
        const isActive = filter;
        const users = isActive ? [...activeUsers] : [...inactiveUsers];

        const sorted = emailSort
            ? users.sort((a, b) => b.email.localeCompare(a.email))
            : users.sort((a, b) => a.email.localeCompare(b.email));

        if (isActive) setActiveUsers(sorted);
        else setInactiveUsers(sorted);

        setEmailSort(!emailSort);
    }

function sortDate() {
    const isActive = filter;
    const users = isActive ? [...activeUsers] : [...inactiveUsers];

    const getTime = (u) => {
        if (!u.date_created) return null;
        const t = new Date(u.date_created).getTime();
        return isNaN(t) ? null : t;
    };

    const sorted = users.sort((a, b) => {
        const aTime = getTime(a);
        const bTime = getTime(b);

        // Both N/A → equal
        if (aTime === null && bTime === null) return 0;

        // One N/A → always push N/A to bottom
        if (aTime === null) return 1;
        if (bTime === null) return -1;

        // Normal date comparison
        return dateSort ? bTime - aTime : aTime - bTime;
    });

    if (isActive) setActiveUsers(sorted);
    else setInactiveUsers(sorted);

    setDateSort(!dateSort);
}


    // Styles
    const searchInputStyle = { float: 'right', margin: '16px', height: '28px', padding: '0px 12px' };
    const errorMessage = { position: 'absolute', top: '4%', fontWeight: 'bold', right: '5%', margin: '16px', height: '28px', padding: '0px 12px', color: 'red' };
    const dropdownStyle = { float: 'right', margin: '16px', height: '32px', padding: '0px 12px' };
    const searchButton = { float: 'right', margin: '16px 0px', height: '32px', padding: '0px 12px', backgroundColor: '#00B3B5', color: 'white', width: 'auto', border: 'none' };
    const exportButton = { margin: '16px 0px', height: '32px', padding: '0px 12px', backgroundColor: '#00B3B5', color: 'white', width: 'auto', border: 'none', display: 'block', position: 'fixed', top: '15%', left: '50%' };
    const caretStyle = { height: '16px', width: 'auto' };
    const copyButtonStyle = { cursor: 'pointer', marginRight: '8px', height: '24px', width: '24px', verticalAlign: 'middle' };

    const renderHoverDetailsModal = (user, position) => {
        if (!user) return null;
        return (
            <div
                className="hoverModal"
                style={{ 
                    position: 'absolute',
                    top: hoverPosition.top - 80,
                    left: hoverPosition.left - 120,
                    zIndex: 9999,
                    pointerEvents: 'auto',
                    transform: 'translateY(-100%)', // move above the row
                    background: 'white',
                    border: '1px solid #ccc',
                }}
                onMouseEnter={() => setIsHoveringModal(true)}
                onMouseLeave={() => {
                    setIsHoveringModal(false);
                    setHoveredUser(null);
                }}
            >

                <h3>User Details</h3>
                {user.name && <p><strong>Name:</strong> {user.name} {user.lastName || ''}</p>}
                {user.email && <p><strong>Email:</strong> {user.email}</p>}
                <p><strong>User Type:</strong> {classifyUser(user)}</p>
                {user.date_created && <p><strong>Date Created:</strong> {new Date(user.date_created).toLocaleString()}</p>}
                {user.phone && <p><strong>Phone #:</strong> {formatPhoneNumber(user.phone)}</p>}
                {user.userRoleSelections?.length > 0 && <p><strong>Roles:</strong> {user.userRoleSelections.join(', ')}</p>}
                {user.otherRole && <p><strong>Other Role:</strong> {user.otherRole}</p>}
                {user.primaryDecisionMaker === true && <p><strong>Primary Decision Maker:</strong> Yes</p>}
                {user.annualRevenue && <p><strong>Annual Revenue:</strong> {user.annualRevenue}</p>}
                {user.goals?.length > 0 && <p><strong>Goals:</strong> {user.goals.join(', ')}</p>}
                {user.otherGoal && <p><strong>Other Goal:</strong> {user.otherGoal}</p>}
            </div>
        );
    };

    function returnUsers() {
        const activeUsersToDisplay = filteredUsers.length > 0 ? filteredUsers : activeUsers;
        const inactiveUsersToDisplay = filteredUsers.length > 0 ? filteredUsers : inactiveUsers;

        const renderRows = (users, isActive = true) => users.map(user => {
            const userType = classifyUser(user);
            return (
                <tr key={user._id}
                    onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const scrollTop = window.scrollY || document.documentElement.scrollTop;
                        const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

                        // Position above the row, with a small offset
                        const top = rect.top + scrollTop - 10; // 10px above row
                        const left = rect.left + scrollLeft;

                        setHoverPosition({ top, left });
                        setHoveredUser(user);
                    }}
                    onMouseLeave={() => {
                        if (!isHoveringModal) setHoveredUser(null);
                    }}
                    style={{ lineHeight: '2.5', cursor: 'default' }}
                >

                    <td style={{ padding: '14px 8px' }}>
                        <img 
                            src={copyIcon} 
                            alt="Copy" 
                            style={copyButtonStyle} 
                            title="Copy Name, Email, User Type"
                            onClick={() => copyToClipboard(`Name: ${user.name} ${user.lastName}\nEmail: ${user.email}\nUser Type: ${userType}`)}
                        />
                        {user.name} {isActive ? user.lastName : ''}
                    </td>
                    <td style={{ padding: '14px 8px' }}>{user.email}</td>
                    <td style={{ padding: '14px 8px', color: getUserTypeColor(userType), fontWeight: 'bold' }}>{userType}</td>
                    <td style={{ padding: '14px 8px' }}>{user.date_created ? new Date(user.date_created).toLocaleDateString() : 'N/A'}</td>
                    <td style={{ padding: '14px 8px' }}>
                        <button onClick={() => goToAccount(user._id)} className="adjustment-button">EDIT</button>
                        {isActive && (
                            (currentUser.email.includes("@pdgcreative.com") ||
                            currentUser.email === "jdiem@getlevlr.com" ||
                            currentUser.email === "dwk2007@gmail.com" ||
                            currentUser.email === "jdiem@elkmontllc.com") && !(
                                (currentUser.email === "jdiem@getlevlr.com" || currentUser.email === "jdiem@elkmontllc.com" || currentUser.email === "dwk2007@gmail.com") &&
                                user.email.includes("@pdgcreative.com")
                            ) && (
                                <button
                                    onClick={() => dispatch(postMasqueradeUser(user.email))}
                                    className="adjustment-button"
                                    style={{ marginLeft: '6px', backgroundColor: '#666', color: 'white' }}
                                >
                                    MASQUERADE
                                </button>
                            )
                        )}
                    </td>
                </tr>
            );
        });

        return filter ? (
            <div>
                <h1>Active Users</h1>
                <p style={{ position: 'fixed', top: '20%', padding: '0 16px', fontWeight: 'bold' }}>
                    Freemium Users: {userTally.freemium}, Subscribed Users: {userTally.subscribed}, Internal Users: {userTally.internal}
                </p>
                <table className={'activeUsers'}>
                    <thead>
                        <tr>
                            <th onClick={() => sortName()}>Name <img style={caretStyle} src={nameSort ? caretDown : caretUp} /></th>
                            <th onClick={() => sortEmail()}>Email <img style={caretStyle} src={emailSort ? caretDown : caretUp} /></th>
                            <th>User Type</th>
                            <th onClick={() => sortDate()}>Date Created <img style={caretStyle} src={dateSort ? caretDown : caretUp} /></th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>{renderRows(activeUsersToDisplay, true)}</tbody>
                </table>
                {hoveredUser && renderHoverDetailsModal(hoveredUser, hoverPosition)}
            </div>
        ) : (
            <div className={'usercontainer2'}>
                <h1>Inactive Users</h1>
                <div className="inactive">
                    <table className={'activeUsers'}>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>User Type</th>
                                <th>Date Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>{renderRows(inactiveUsersToDisplay, false)}</tbody>
                    </table>
                    {hoveredUser && renderHoverDetailsModal(hoveredUser, hoverPosition)}
                </div>
            </div>
        );
    }

    return (
        <div className={'outer-container'}>
            <MainWrapper />
            <div className={'right-container'}>
                <div className={'admin-container'}>
                    <button style={searchButton} onClick={search}>Search</button>
                    <input placeholder="email" className="users-select" style={searchInputStyle} type="text" onChange={(e) => setSearchByEmail(e.target.value.trim())}/>
                    {matches && <p style={errorMessage}>{matches}</p>}
                    <select className="users-select" style={dropdownStyle} name="filter" id="filter" onChange={handleFilterChange}>
                        <option value="true">Active Users</option>
                        <option value="false">Inactive Users</option>
                    </select>
                    <button style={exportButton} onClick={exportToCSV} disabled={!activeUsers.length}>
                        Export to CSV
                    </button>
                    {returnUsers()}
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
